import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { dashboardService, leadService, opportunityService } from '../../services/api';

// Async thunks
export const fetchDashboardStats = createAsyncThunk(
  'dashboard/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      // Try to get dashboard stats from a dedicated endpoint
      try {
        const response = await dashboardService.getStats();
        return response.data;
      } catch (dashboardError) {
        // If dashboard endpoint doesn't exist, fallback to individual API calls
        console.log('Dashboard stats endpoint not available, falling back to individual counts');
        
        try {
          // Only use leads API since it's working, provide fallback for others
          const leadsResponse = await leadService.getLeads({ limit: 1 });
          
          // Extract count from response - leads API returns array
          const leadsCount = Array.isArray(leadsResponse.data) ? leadsResponse.data.length : 0;
          
          // Get total count by fetching all leads (for now, until pagination is implemented)
          const allLeadsResponse = await leadService.getLeads();
          const totalLeadsCount = Array.isArray(allLeadsResponse.data) ? allLeadsResponse.data.length : 0;
          
          return {
            leads_count: totalLeadsCount,
            opportunities_count: 0, // No API available yet
            customers_count: 0, // No API available yet
            sales_orders_count: 0, // No API available yet
            revenue_mtd: 0, // No API available yet
          };
        } catch (apiError) {
          console.error('Error fetching from individual APIs:', apiError);
          
          // Ultimate fallback with zeros
          return {
            leads_count: 0,
            opportunities_count: 0,
            customers_count: 0,
            sales_orders_count: 0,
            revenue_mtd: 0,
          };
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      return rejectWithValue(error.response?.data || 'Failed to fetch dashboard stats');
    }
  }
);

export const fetchRecentLeads = createAsyncThunk(
  'dashboard/fetchRecentLeads',
  async (limit = 5, { rejectWithValue }) => {
    try {
      const response = await leadService.getLeads({ limit, ordering: '-created_at' });
      return Array.isArray(response.data) ? response.data : (response.data.results || []);
    } catch (error) {
      console.error('Error fetching recent leads:', error);
      // Return empty array instead of rejecting to prevent dashboard errors
      return [];
    }
  }
);

export const fetchRecentOpportunities = createAsyncThunk(
  'dashboard/fetchRecentOpportunities',
  async (limit = 5, { rejectWithValue }) => {
    try {
      const response = await opportunityService.getOpportunities({ limit, ordering: '-created_at' });
      return Array.isArray(response.data) ? response.data : (response.data.results || []);
    } catch (error) {
      console.error('Error fetching recent opportunities:', error);
      // Return empty array instead of rejecting to prevent dashboard errors
      return [];
    }
  }
);

// Initial state
const initialState = {
  stats: {
    leads_count: 0,
    opportunities_count: 0,
    sales_orders_count: 0,
    revenue_mtd: 0,
  },
  recentLeads: [],
  recentOpportunities: [],
  isLoading: false,
  error: null,
  lastUpdated: null,
};

// Dashboard slice
const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetDashboard: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Dashboard Stats
      .addCase(fetchDashboardStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.stats = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch dashboard stats';
      })
      
      // Fetch Recent Leads
      .addCase(fetchRecentLeads.fulfilled, (state, action) => {
        state.recentLeads = action.payload;
      })
      
      // Fetch Recent Opportunities
      .addCase(fetchRecentOpportunities.fulfilled, (state, action) => {
        state.recentOpportunities = action.payload;
      });
  },
});

export const { clearError, resetDashboard } = dashboardSlice.actions;
export default dashboardSlice.reducer;